import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import Datepicker from "react-tailwindcss-datepicker";
import Modal from "react-bootstrap/Modal";
import { saveAs } from "file-saver";
import { toBlob } from "html-to-image";
import API from "../../services/api";
import { clampCurrentMonthDateRange, formatReportDate, getCurrentReportMonthDateRange } from "./reportDateUtils";

// Languages selected on first load (Telugu, Tamil).
const DEFAULT_LANG_IDS = [1, 2];

const SLOT_LETTERS = ["A", "B", "C"];
const METRICS = [
  { key: "adv_impressions", label: "Adv Imressions" },
  { key: "adv_clicks", label: "Adv clicks" },
  { key: "revenue", label: "Revenue" },
];

const COLORS = {
  headerBg: "#0B4F8A",
  dateText: ["#FFFF00", "#FF00FF"],
  labelBg: "#EDEDED",
  langBg: ["#D9D2E9", "#FFF2CC"],
};

// Absolute number with Indian digit grouping; blank for zero/missing.
const fmt = (value) => (value ? Math.round(value).toLocaleString("en-IN") : "");

const cellStyle = { minWidth: 90, textAlign: "right" };

// Header rows are a fixed height so each can stick just below the one above.
const HEAD_ROW_HEIGHT = 34;
const FIRST_COL_WIDTH = 90;

// Separate borders (not collapsed) so borders stay visible on sticky cells.
const TABLE_CSS = `
#position-wise-report { border-collapse: separate; border-spacing: 0; width: max-content; border-top: 1px solid #dee2e6; border-left: 1px solid #dee2e6; }
#position-wise-report th, #position-wise-report td { border-right: 1px solid #dee2e6; border-bottom: 1px solid #dee2e6; padding: 0 6px; white-space: nowrap; box-sizing: border-box; }
`;

const stickyHead = (row, extra = {}) => ({
  position: "sticky",
  top: row * HEAD_ROW_HEIGHT,
  height: HEAD_ROW_HEIGHT,
  zIndex: 3,
  ...extra,
});

const stickyFirstCol = (extra = {}) => ({
  position: "sticky",
  left: 0,
  zIndex: 2,
  minWidth: FIRST_COL_WIDTH,
  ...extra,
});

// Widest range allowed; each day adds languages x 3 columns to the table.
const MAX_RANGE_DAYS = 31;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const toTimestamp = (d) => Math.floor(startOfDay(d).getTime() / 1000);

// "YYYY-MM-DD" -> local Date
const parseDate = (value) => {
  const [y, m, d] = String(value).split("-").map(Number);
  return new Date(y, m - 1, d);
};

function PositionWiseReport() {
  const [allLanguages, setAllLanguages] = useState([]);
  const [selectedLangs, setSelectedLangs] = useState([]);
  const langDefaultApplied = useRef(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Cell whose campaign breakup is open: { date, label, lang, position, slot, letter }
  const [breakup, setBreakup] = useState(null);
  const [breakupRows, setBreakupRows] = useState([]);
  const [breakupLoading, setBreakupLoading] = useState(false);
  // Current month up to yesterday, matching the other reports.
  const [dateRange, setDateRange] = useState(getCurrentReportMonthDateRange);
  const fromDate = useMemo(() => (dateRange?.startDate ? parseDate(dateRange.startDate) : null), [dateRange]);
  const toDate = useMemo(() => (dateRange?.endDate ? parseDate(dateRange.endDate) : null), [dateRange]);

  const handleDateRange = (selected) => {
    if (!selected?.startDate || !selected?.endDate) return;
    let range = clampCurrentMonthDateRange(selected);
    // Cap the span so the table stays a manageable width.
    const start = parseDate(range.startDate);
    const maxEnd = new Date(start.getFullYear(), start.getMonth(), start.getDate() + MAX_RANGE_DAYS - 1);
    if (parseDate(range.endDate) > maxEnd) {
      range = { startDate: range.startDate, endDate: formatReportDate(maxEnd) };
    }
    setDateRange(range);
  };

  useEffect(() => {
    if (!fromDate || !toDate) return;
    const startTimestamp = toTimestamp(fromDate);
    const endTimestamp = toTimestamp(toDate);
    setLoading(true);
    API({
      url: "/reports/positionwisereport",
      method: "POST",
      responseType: "json",
      data: { startTimestamp, endTimestamp },
    })
      .then((res) => {
        if (res.code === 200) {
          const langs = res.data?.languages || [];
          setAllLanguages(langs);
          // Apply the default once; after that the user's choice (even "all") sticks.
          // Wait for a response that actually has languages, so an empty one can't use it up.
          if (!langDefaultApplied.current && langs.length) {
            langDefaultApplied.current = true;
            setSelectedLangs(langs.filter((l) => DEFAULT_LANG_IDS.includes(l.value)));
          }
          setRows(res.data?.rows || []);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  const openBreakup = (cell) => {
    setBreakup(cell);
    setBreakupRows([]);
    setBreakupLoading(true);
    API({
      url: "/reports/positionwisereportbreakup",
      method: "POST",
      responseType: "json",
      data: { date: cell.ts, lang_id: cell.lang.value, position: cell.position, slot: cell.slot },
    })
      .then((res) => {
        if (res.code === 200) setBreakupRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch((error) => console.error(error))
      .finally(() => setBreakupLoading(false));
  };

  const [imageLoading, setImageLoading] = useState(false);

  const fileName = () => `position_wise_report_${dateRange.startDate}_to_${dateRange.endDate}`;

  // Mirrors the table layout. CSV cannot merge cells, so a date / language label
  // sits in the first column of its group and the rest of the group is blank.
  const downloadCsv = () => {
    const quote = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const perDay = languages.length * METRICS.length;

    const dateRow = ["Dates"];
    const langRow = ["Positions"];
    const metricRow = [""];
    days.forEach(({ label }) => {
      dateRow.push(label, ...Array(perDay - 1).fill(""));
      languages.forEach((l) => {
        langRow.push(l.label, ...Array(METRICS.length - 1).fill(""));
        METRICS.forEach((m) => metricRow.push(m.label));
      });
    });

    const spacer = Array(1 + days.length * perDay).fill("");
    const lines = [dateRow, langRow, metricRow];
    positions.forEach((position) => {
      SLOT_LETTERS.forEach((letter, slot) => {
        const line = [`${position}${letter}`];
        days.forEach(({ ts }) =>
          languages.forEach((l) => {
            const cell = lookup.get(`${ts}|${l.value}|${position}|${slot}`);
            METRICS.forEach((m) => line.push(cell ? Math.round(cell[m.key] || 0) : ""));
          })
        );
        lines.push(line);
      });
      lines.push(spacer);
    });

    const csv = lines.map((line) => line.map(quote).join(",")).join("\n");
    saveAs(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }), `${fileName()}.csv`);
  };

  const downloadImage = async () => {
    const node = document.getElementById("position-wise-report");
    if (!node) return;
    setImageLoading(true);
    try {
      // Capture the table itself, not its scroll box, so the whole report is included.
      const blob = await toBlob(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 1,
        width: node.scrollWidth,
        height: node.scrollHeight,
      });
      if (!blob) throw new Error("Empty image");
      saveAs(blob, `${fileName()}.png`);
    } catch (error) {
      console.error(error);
      alert("Could not generate the image. The report may be too wide - try a shorter date range or fewer languages.");
    } finally {
      setImageLoading(false);
    }
  };

  // Empty selection means all languages.
  const languages = useMemo(
    () =>
      selectedLangs.length
        ? allLanguages.filter((l) => selectedLangs.some((s) => s.value === l.value))
        : allLanguages,
    [allLanguages, selectedLangs]
  );

  const days = useMemo(() => {
    if (!fromDate || !toDate) return [];
    const list = [];
    // Step by calendar day rather than 86400s so a DST shift can't skip a date.
    for (let d = startOfDay(fromDate); d <= toDate; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      list.push({ ts: toTimestamp(d), label: `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}` });
    }
    return list;
  }, [fromDate, toDate]);

  // `${date}|${lang}|${position}|${slot}` -> metrics
  const lookup = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      map.set(`${r.date}|${r.lang_id}|${r.position}|${r.slot}`, r);
    });
    return map;
  }, [rows]);

  const positions = useMemo(
    () => [...new Set(rows.map((r) => r.position))].sort((a, b) => a - b),
    [rows]
  );

   const metricColSpan = METRICS.length;
  const labelCell = { background: COLORS.labelBg, fontWeight: "bold", textAlign: "center" };

  const filters = (
      <div className="d-flex gap-3 align-items-end flex-wrap mb-3">
      <div style={{ minWidth: 280 }}>
        <label className="form-label d-block">Date Range (max {MAX_RANGE_DAYS} days)</label>
        <div className="border rounded-lg border-gray-300 hover:cursor-pointer hover:border-2 hover:border-sky-500">
          <Datepicker
            classNames="form-input"
            value={dateRange}
            primaryColor={"cyan"}
            showShortcuts={true}
            useRange={true}
            maxDate={new Date()}
            configs={{
              shortcuts: {
                today: "today",
                yesterday: "yesterday",
                currentMonth: "currentMonth",
                pastMonth: "lastMonth",
              },
            }}
            onChange={handleDateRange}
          />
        </div>
      </div>
      <div style={{ minWidth: 300 }}>
        <label className="form-label">Filter by Language</label>
        <Select
          isMulti
          options={allLanguages}
          value={selectedLangs}
          onChange={(selected) => setSelectedLangs(selected || [])}
          placeholder="All Languages"
          // Render the menu on body: the sticky table headers sit above it otherwise.
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
      </div>
      {rows.length > 0 && (
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={downloadCsv}>
            Download CSV
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={downloadImage}
            disabled={imageLoading}
          >
            {imageLoading ? "Preparing..." : "Download Image"}
          </button>
        </div>
      )}
      </div>
  );

  if (!rows.length) {
    return (
      <>
        {filters}
        <div className="my-3">{loading ? "Loading..." : "No data found"}</div>
      </>
    );
  }

  return (
    <div className="report-image">
      {filters}
      <style>{TABLE_CSS}</style>
      <div className="responsive-table" style={{ overflow: "auto", maxHeight: "75vh" }}>
        <table className="table report-table mb-0" id="position-wise-report">
          <thead>
            <tr>
              <th
                style={stickyHead(0, {
                  ...stickyFirstCol(),
                  ...labelCell,
                  zIndex: 5,
                  background: COLORS.headerBg,
                  color: "#fff",
                })}
              >
                Dates
              </th>
              {days.map(({ ts, label }, d) => (
                <th
                  key={ts}
                  colSpan={languages.length * metricColSpan}
                  style={stickyHead(0, {
                    background: COLORS.headerBg,
                    color: COLORS.dateText[d % 2],
                    textAlign: "center",
                  })}
                >
                  {label}
                </th>
              ))}
            </tr>
            <tr>
              <th style={stickyHead(1, { ...stickyFirstCol(), ...labelCell, zIndex: 5 })}>Positions</th>
              {days.map(({ ts }) =>
                languages.map((l, li) => (
                  <th
                    key={`${ts}-${l.value}`}
                    colSpan={metricColSpan}
                    style={stickyHead(1, { background: COLORS.langBg[li % 2], textAlign: "center" })}
                  >
                    {l.label}
                  </th>
                ))
              )}
            </tr>
            <tr>
              <th style={stickyHead(2, { ...stickyFirstCol(), ...labelCell, zIndex: 5 })}></th>
              {days.map(({ ts }) =>
                languages.map((l) =>
                  METRICS.map((m) => (
                    <th key={`${ts}-${l.value}-${m.key}`} style={stickyHead(2, { ...labelCell, minWidth: 90 })}>
                      {m.label}
                    </th>
                  ))
                )
              )}
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => (
              <React.Fragment key={position}>
                {SLOT_LETTERS.map((letter, slot) => (
                  <tr key={`${position}${letter}`}>
                    <td style={stickyFirstCol(labelCell)}>{`${position}${letter}`}</td>
                    {days.map(({ ts, label }) =>
                      languages.map((l) => {
                        const cell = lookup.get(`${ts}|${l.value}|${position}|${slot}`);
                        return METRICS.map((m) => (
                          <td
                            key={`${ts}-${l.value}-${m.key}`}
                            style={cell ? { ...cellStyle, cursor: "pointer" } : cellStyle}
                            title={cell ? "Click for campaign breakup" : undefined}
                            onClick={
                              cell
                                ? () => openBreakup({ ts, label, lang: l, position, slot, letter })
                                : undefined
                            }
                          >
                            {fmt(cell?.[m.key])}
                          </td>
                        ));
                      })
                    )}
                  </tr>
                ))}
                <tr>
                  <td style={stickyFirstCol({ background: COLORS.labelBg })}>&nbsp;</td>
                  <td colSpan={days.length * languages.length * metricColSpan}></td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <Modal show={!!breakup} onHide={() => setBreakup(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {breakup &&
              `${breakup.position}${breakup.letter} - ${breakup.lang.label} - ${breakup.label}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Type</th>
                <th style={{ textAlign: "right" }}>Adv Imressions</th>
                <th style={{ textAlign: "right" }}>Adv clicks</th>
                <th style={{ textAlign: "right" }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {breakupRows.map((r) => (
                <tr key={r.campaign_id}>
                  <td>{r.campaign_name ? `${r.campaign_name} (${r.campaign_id})` : r.campaign_id}</td>
                  <td>{r.campaign_type || "-"}</td>
                  <td style={{ textAlign: "right" }}>{fmt(r.adv_impressions) || 0}</td>
                  <td style={{ textAlign: "right" }}>{fmt(r.adv_clicks) || 0}</td>
                  <td style={{ textAlign: "right" }}>{fmt(r.revenue) || 0}</td>
                </tr>
              ))}
              {!breakupRows.length && (
                <tr>
                  <td colSpan={5} className="text-center">
                    {breakupLoading ? "Loading..." : "No campaigns found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default PositionWiseReport;
